"use client";

import { c as _c } from "react/compiler-runtime";
import { createInstance, type i18n as I18nInstanceType, type Resource, type TFunction } from "i18next"; // Renamed i18n to I18nInstanceType, imported TFunction
import HttpBackend from "i18next-http-backend"; // 客户端使用 ESM 版本
import { useAtomValue, useSetAtom } from "jotai";
import type React from "react";
import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { initReactI18next } from "react-i18next/initReactI18next";
import { currentLanguageAtom } from "@/store/languageAtom";
import { backendOptions, getOptions } from "../lib/i18n/settings"; // 调整路径

// Module-level variable to hold the i18n instance
let globalI18nInstance: I18nInstanceType | null = null;
let fallbackInstance: I18nInstanceType | null = null;
const getFallbackT = (): TFunction => {
  if (!fallbackInstance) {
    fallbackInstance = createInstance();
    // Minimal init to get a t function that returns keys
    fallbackInstance.init({
      lng: "cimode",
      // Using 'cimode' will return keys
      fallbackLng: false,
      resources: {},
      ns: [],
      defaultNS: false
    });
  }
  return fallbackInstance.t.bind(fallbackInstance);
};

// 客户端初始化函数 (与服务端类似，但使用 ESM HttpBackend)
const initI18nextClient = async (lng: string, ns: string | string[], resources: Resource): Promise<I18nInstanceType> => {
  const i18nInstance = createInstance();
  await i18nInstance.use(initReactI18next).use(HttpBackend).init({
    ...getOptions(lng, Array.isArray(ns) ? ns[0] : ns),
    resources,
    // 传入从服务端加载的初始资源
    lng,
    ns,
    backend: backendOptions
    // debug: true,
  });
  return i18nInstance;
};
interface TranslationsProviderProps {
  children: React.ReactNode;
  locale: string;
  namespaces: string | string[];
  resources: Resource;
}
export default function TranslationsProvider(t0) {
  const $ = _c(14);
  const {
    children,
    locale,
    namespaces,
    resources
  } = t0;
  const [i18nProviderInstance, setI18nProviderInstance] = useState(null);
  const currentLanguage = useAtomValue(currentLanguageAtom);
  const setCurrentLanguage = useSetAtom(currentLanguageAtom);
  let t1;
  let t2;
  if ($[0] !== currentLanguage || $[1] !== locale || $[2] !== setCurrentLanguage) {
    t1 = () => {
      const preferredLanguage = currentLanguage || locale.toLowerCase();
      setCurrentLanguage(preferredLanguage);
    };
    t2 = [locale, currentLanguage, setCurrentLanguage];
    $[0] = currentLanguage;
    $[1] = locale;
    $[2] = setCurrentLanguage;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== currentLanguage || $[6] !== locale || $[7] !== namespaces || $[8] !== resources) {
    t3 = () => {
      const initialize = async () => {
        const preferredLanguage_0 = currentLanguage || locale.toLowerCase();
        const newInstance = await initI18nextClient(preferredLanguage_0, namespaces, resources);
        setI18nProviderInstance(newInstance);
        globalI18nInstance = newInstance;
      };
      initialize();
    };
    t4 = [locale, namespaces, resources, currentLanguage];
    $[5] = currentLanguage;
    $[6] = locale;
    $[7] = namespaces;
    $[8] = resources;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  if (!i18nProviderInstance || !i18nProviderInstance.isInitialized) {
    return null;
  }
  const t5 = children as any;
  let t6;
  if ($[11] !== i18nProviderInstance || $[12] !== t5) {
    t6 = <I18nextProvider i18n={i18nProviderInstance}>{t5}</I18nextProvider>;
    $[11] = i18nProviderInstance;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

/**
 * Retrieves the t function from the global i18next instance.
 * Provides a fallback t function that returns the key if the instance is not ready.
 * @returns The t function or a fallback.
 */
export const getT = (): TFunction => {
  if (globalI18nInstance?.isInitialized && typeof globalI18nInstance.t === "function") {
    return globalI18nInstance.t.bind(globalI18nInstance);
  }
  return getFallbackT();
};